"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useQueryClient } from "@tanstack/react-query"
import { Sparkles, Loader2 } from "lucide-react"
import { createPost, updatePost } from "@/lib/supabase/queries"
import type { CreatePostInput, UpdatePostInput } from "@/lib/supabase/queries"
import { generateBlogMetadata } from "@/services/editorAIService"
import { TagMultiSelect } from "./TagMultiSelect"
import { TiptapEditor } from "./TiptapEditor"
import { cn } from "@/lib/utils"

interface BlogTagAdmin {
  id: string
  name: string
  slug: string
  createdAt: Date
}

interface BlogPostWithTags {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  author: string | null
  published: boolean
  publishedAt: Date | null
  createdAt: Date
  updatedAt: Date | null
  tags: { id: string; name: string; slug: string; createdAt: Date }[]
}

interface PostFormProps {
  post?: BlogPostWithTags
  tags: BlogTagAdmin[]
  formId?: string
  onSubmittingChange?: (isSubmitting: boolean) => void
}

export function PostForm({ post, tags, formId = "post-form", onSubmittingChange }: PostFormProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const isEditing = !!post

  const [title, setTitle] = useState(post?.title || "")
  const [slug, setSlug] = useState(post?.slug || "")
  const [excerpt, setExcerpt] = useState(post?.excerpt || "")
  const [content, setContent] = useState(post?.content || "")
  const [author, setAuthor] = useState(post?.author || "Specledger")
  const [selectedTags, setSelectedTags] = useState<string[]>(
    post?.tags.map((t) => t.id) || []
  )
  const [published, setPublished] = useState(post?.published || false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(isEditing)
  const [isGeneratingAI, setIsGeneratingAI] = useState(false)

  const updateSubmitting = (value: boolean) => {
    setIsSubmitting(value)
    onSubmittingChange?.(value)
  }

  const handleAIGenerate = async () => {
    if (!content.trim()) {
      setError("Please write some content first to generate metadata.")
      return
    }

    setIsGeneratingAI(true)
    setError("")

    try {
      const metadata = await generateBlogMetadata(content)
      setTitle(metadata.title)
      setExcerpt(metadata.excerpt)
      if (!slugManuallyEdited) {
        setSlug(generateSlug(metadata.title))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate metadata")
    } finally {
      setIsGeneratingAI(false)
    }
  }

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
  }

  const handleTitleChange = (value: string) => {
    setTitle(value)
    if (!slugManuallyEdited) {
      setSlug(generateSlug(value))
    }
  }

  const handleSlugChange = (value: string) => {
    setSlug(value)
    setSlugManuallyEdited(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    updateSubmitting(true)

    try {
      if (isEditing) {
        const input: UpdatePostInput = {
          id: post.id,
          title,
          slug,
          excerpt,
          content,
          author: author || undefined,
          tags: selectedTags,
          published,
        }
        const result = await updatePost(input)
        if (result.success) {
          queryClient.invalidateQueries({ queryKey: ["admin-posts"] })
          queryClient.invalidateQueries({ queryKey: ["admin-post", post.id] })
          router.push("/admin")
        } else {
          setError(result.error || "Failed to update post")
        }
      } else {
        const input: CreatePostInput = {
          title,
          slug,
          excerpt,
          content,
          author: author || undefined,
          tags: selectedTags,
          published,
        }
        const result = await createPost(input)
        if (result.success) {
          queryClient.invalidateQueries({ queryKey: ["admin-posts"] })
          router.push("/admin")
        } else {
          setError(result.error || "Failed to create post")
        }
      }
    } catch {
      setError("An unexpected error occurred")
    } finally {
      updateSubmitting(false)
    }
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content - Editor only */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <label className="block text-sm font-medium text-text mb-2">
              Content
            </label>
            <TiptapEditor
              content={content}
              onChange={setContent}
              placeholder="Write your post content..."
            />
          </div>
        </div>

        {/* Sidebar - Post Settings */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-text">Post Settings</h3>
              <button
                type="button"
                onClick={handleAIGenerate}
                disabled={isGeneratingAI || !content.trim()}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors",
                  "bg-gradient-to-r from-purple-500 to-indigo-500 text-white",
                  "hover:from-purple-600 hover:to-indigo-600",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isGeneratingAI ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                {isGeneratingAI ? "Generating..." : "AI Generate"}
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Excerpt
                </label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  required
                  rows={3}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Author
                </label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Tags
                </label>
                <TagMultiSelect
                  tags={tags}
                  selected={selectedTags}
                  onChange={setSelectedTags}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20"
                />
                <label htmlFor="published" className="text-sm text-text">
                  Published
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  )
}
